import React, { useState } from 'react';
import { CreditCard, Link2, Check, Clock, Globe, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Reusable Payment Method Selector Component
 * 
 * Used in:
 * - AddBookingWizard (Step 6) - New bookings
 * - EditBookingModal (Payment tab) - Adding extras to existing bookings
 * 
 * Props:
 * - amount: number - Amount to collect
 * - currency: string - Default currency (e.g., 'USD')
 * - supportedCurrencies: array - Available currencies for multi-currency [{code, symbol, name}]
 * - selectedMethod: string - Currently selected method
 * - onMethodChange: (method) => void - Callback when method changes
 * - selectedCurrency: string - Selected currency for Stripe payments
 * - onCurrencyChange: (currency) => void - Callback when currency changes
 * - showCurrencySelector: boolean - Show multi-currency options (default: true)
 * - disabled: boolean - Disable all interactions
 * - compact: boolean - Use compact layout (default: false)
 */

const PAYMENT_METHODS = [
  {
    id: 'pay_now_redirect',
    label: 'Pay Now',
    description: 'Enter card details via Stripe (over phone)',
    icon: CreditCard,
    color: 'blue',
    requiresStripe: true
  },
  {
    id: 'send_link',
    label: 'Share Payment Link',
    description: 'Send Stripe link to customer via SMS/Email',
    icon: Link2,
    color: 'purple',
    requiresStripe: true
  },
  {
    id: 'paid',
    label: 'Already Paid',
    description: 'Payment collected externally (cash, check, etc.)',
    icon: Check,
    color: 'green',
    requiresStripe: false
  },
  {
    id: 'unpaid',
    label: 'Will Pay Later',
    description: 'Deposit, on arrival, invoice, etc.',
    icon: Clock,
    color: 'yellow',
    requiresStripe: false
  }
];

const COLOR_CLASSES = {
  blue: {
    border: 'border-blue-600',
    bg: 'bg-blue-50',
    hoverBorder: 'hover:border-blue-300',
    iconBg: 'bg-blue-600',
    iconBgInactive: 'bg-gray-100',
    iconText: 'text-white',
    iconTextInactive: 'text-gray-600',
    checkmark: 'text-blue-600'
  },
  purple: {
    border: 'border-purple-600',
    bg: 'bg-purple-50',
    hoverBorder: 'hover:border-purple-300',
    iconBg: 'bg-purple-600',
    iconBgInactive: 'bg-gray-100',
    iconText: 'text-white',
    iconTextInactive: 'text-gray-600',
    checkmark: 'text-purple-600'
  },
  green: {
    border: 'border-green-600',
    bg: 'bg-green-50',
    hoverBorder: 'hover:border-green-300',
    iconBg: 'bg-green-600',
    iconBgInactive: 'bg-gray-100',
    iconText: 'text-white',
    iconTextInactive: 'text-gray-600',
    checkmark: 'text-green-600'
  },
  yellow: {
    border: 'border-yellow-600',
    bg: 'bg-yellow-50',
    hoverBorder: 'hover:border-yellow-300',
    iconBg: 'bg-yellow-600',
    iconBgInactive: 'bg-gray-100',
    iconText: 'text-white',
    iconTextInactive: 'text-gray-600',
    checkmark: 'text-yellow-600'
  }
};

const PaymentMethodSelector = ({
  amount = 0,
  currency = 'USD',
  supportedCurrencies = [],
  selectedMethod = '',
  onMethodChange,
  selectedCurrency = '',
  onCurrencyChange,
  showCurrencySelector = true,
  disabled = false,
  compact = false
}) => {
  const isStripeMethod = selectedMethod === 'pay_now_redirect' || selectedMethod === 'send_link';
  const showCurrencies = showCurrencySelector && isStripeMethod && supportedCurrencies.length > 0;

  return (
    <div className="space-y-4">
      {/* Payment Method Options */}
      <div className={`grid gap-3 ${compact ? 'grid-cols-2' : ''}`}>
        {PAYMENT_METHODS.map((method) => {
          const isSelected = selectedMethod === method.id;
          const colors = COLOR_CLASSES[method.color];
          const Icon = method.icon;

          return (
            <div
              key={method.id}
              onClick={() => !disabled && onMethodChange(method.id)}
              className={`
                p-4 border rounded-lg cursor-pointer transition-all
                ${isSelected ? `${colors.border} ${colors.bg}` : `border-gray-200 ${colors.hoverBorder}`}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              data-testid={`payment-method-${method.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${isSelected ? colors.iconBg : colors.iconBgInactive}
                  `}>
                    <Icon className={`w-5 h-5 ${isSelected ? colors.iconText : colors.iconTextInactive}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{method.label}</p>
                    {!compact && (
                      <p className="text-xs text-gray-600">{method.description}</p>
                    )}
                  </div>
                </div>
                {isSelected && <Check className={`w-5 h-5 ${colors.checkmark}`} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Multi-Currency Selector - Only for Stripe payment options */}
      {showCurrencies && (
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Customer&apos;s Currency</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {supportedCurrencies.map((curr) => (
              <button
                key={curr.code}
                onClick={() => !disabled && onCurrencyChange(curr.code)}
                disabled={disabled}
                className={`
                  px-3 py-2 rounded-lg text-sm font-medium transition-all
                  ${selectedCurrency === curr.code 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-blue-300'}
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                data-testid={`currency-${curr.code}`}
              >
                {curr.symbol} {curr.code}
              </button>
            ))}
          </div>
          {selectedCurrency && selectedCurrency !== currency && (
            <p className="text-xs text-gray-500 mt-2">
              Amount will be converted to {selectedCurrency} at checkout
            </p>
          )}
        </div>
      )}

      {/* Amount Display */}
      {amount > 0 && (
        <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
          <span className="text-sm font-medium text-gray-600">Amount to Collect</span>
          <span className="text-lg font-bold text-gray-900">
            {new Intl.NumberFormat('en-US', { 
              style: 'currency', 
              currency: selectedCurrency || currency 
            }).format(amount)}
          </span>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;
